import React from 'react';
import { Checkbox, Segmented, Typography } from 'antd';

import {
	EXPORT_RECORDS_CAP,
	EXPORT_RECORDS_EMBED_CAP,
	EXPORT_ROW_CAP,
} from '../../services/exports';

const { Text } = Typography;

/**
 * THE ONE PLACE THE EXPORT QUESTIONS ARE ASKED.
 *
 * Two surfaces ask them — the export dialog ("give me this now",
 * `ExportModal`) and the stored-export editor ("send me this on the 1st",
 * `collections/ExportDefinitionEditor`) — and they must ask in the same words
 * with the same choices, or the second surface quietly stops offering
 * something the first one does.
 *
 * Controlled, and stateless: the caller owns every value, because one caller
 * throws its answers away when the dialog closes and the other stores them.
 *
 *   format          'pdf' | 'json' | 'xlsx'
 *   contents        what a PDF of a LIST holds: one of `listOptions`' values
 *                   ('list' in the dialog — "the view you are looking at" —
 *                   or 'table' / 'grid' in the editor, which has no view),
 *                   'records' (full item pages) or 'matrix' (the report grid)
 *   attachmentList · embedAttachments   the item-page attachment options
 *   blankForm       the report grid printed empty, to fill in by hand
 *   groupShape      Excel's layout of a repeating group (KPR-685): 'item' — one
 *                   line per item, the group in one cell — or 'entries', one
 *                   line per entry. Asked only when `offersGroupShape` (a card
 *                   in the export's scope holds a group).
 */

/** Excel's two shapes for a repeating group (keepr-api xlsxRender GROUP_SHAPES). */
export const GROUP_SHAPE_OPTIONS = [
	{ label: 'One line per item', value: 'item' },
	{ label: 'One line per entry', value: 'entries' },
];

/** The dialog's single list choice: whichever view is on screen. */
export const VIEW_LIST_OPTIONS = [ { label: 'List (current view)', value: 'list' } ];
/** A stored export has no screen to copy, so it names the layout. */
export const STORED_LIST_OPTIONS = [
	{ label: 'Table', value: 'table' },
	{ label: 'Grid', value: 'grid' },
];

/** The item cap that applies to a configuration (null: not a list). */
export function exportCapFor({ format, contents, embedAttachments, isItemsTarget }) {
	if (!isItemsTarget) return null;
	if (format === 'pdf' && contents === 'records') {
		return embedAttachments ? EXPORT_RECORDS_EMBED_CAP : EXPORT_RECORDS_CAP;
	}
	return EXPORT_ROW_CAP;
}

export default function ExportOptionsFields({
	format,
	onFormatChange,
	contents,
	onContentsChange,
	attachmentList,
	onAttachmentListChange,
	embedAttachments,
	onEmbedAttachmentsChange,
	blankForm,
	onBlankFormChange,
	groupShape = 'item',
	onGroupShapeChange,
	// What this target can express at all.
	offersGroupShape = false,
	supportsExcel = false,
	supportsAttachmentOptions = false,
	isItemsTarget = false,
	// A report preset gives the grid its shape; without one there is nothing
	// to lay out, so the choice is not offered.
	report = null,
	listOptions = VIEW_LIST_OPTIONS,
	showCapHint = true,
}) {
	const showAttachmentOptions = supportsAttachmentOptions
		|| (isItemsTarget && contents === 'records');
	const capInPlay = exportCapFor({ format, contents, embedAttachments, isItemsTarget });

	const formatOptions = [
		{ label: 'PDF', value: 'pdf' },
		{ label: 'JSON', value: 'json' },
		...(supportsExcel ? [{ label: 'Excel', value: 'xlsx' }] : []),
	];

	return (
		<>
			<Segmented
				className='export-modal__format'
				options={formatOptions}
				value={format}
				onChange={onFormatChange}
				aria-label='Format'
			/>
			{isItemsTarget && format === 'pdf' ? (
				<Segmented
					className='export-modal__contents'
					options={[
						...listOptions,
						{ label: 'Full item pages', value: 'records' },
						// Offered only where a shape exists to render: a
						// report preset says what the rows and columns are.
						...(report ? [ { label: 'Report grid', value: 'matrix' } ] : []),
					]}
					value={contents}
					onChange={onContentsChange}
					aria-label='What the PDF holds'
				/>
			) : null}
			{/* The paper fallback. A home whose tablet dies still has to
			    give the medication, and the shift filling a blank grid by
			    hand is why this is not an afterthought. */}
			{isItemsTarget && format === 'pdf' && contents === 'matrix' ? (
				<div className='export-modal__options'>
					<Checkbox checked={blankForm} onChange={event => onBlankFormChange(event.target.checked)}>
						Print it blank, to fill in by hand
					</Checkbox>
				</div>
			) : null}
			{format === 'xlsx' && isItemsTarget && offersGroupShape ? (
				<div className='export-modal__option export-modal__groups'>
					<Segmented
						className='export-modal__contents'
						options={GROUP_SHAPE_OPTIONS}
						value={groupShape === 'entries' ? 'entries' : 'item'}
						onChange={onGroupShapeChange}
						aria-label='Repeating groups'
					/>
					<Text type='secondary' className='export-modal__hint'>
						{groupShape === 'entries'
							? 'Each entry of a repeating group on a line of its own, its item’s columns repeated.'
							: 'A repeating group’s entries in one cell, one per line.'}
					</Text>
				</div>
			) : null}
			{format === 'pdf' && showAttachmentOptions ? (
				<div className='export-modal__options'>
					<Checkbox
						checked={attachmentList}
						onChange={event => onAttachmentListChange(event.target.checked)}
					>
						Include attachment list
					</Checkbox>
					<div className='export-modal__option'>
						<Checkbox
							checked={embedAttachments}
							onChange={event => onEmbedAttachmentsChange(event.target.checked)}
						>
							Embed attachments
						</Checkbox>
						<Text type='secondary' className='export-modal__hint'>
							Adds photos and PDF pages to the document.
						</Text>
					</div>
				</div>
			) : null}
			{showCapHint && capInPlay !== null ? (
				<Text type='secondary' className='export-modal__cap-hint'>
					{`Includes up to the first ${capInPlay.toLocaleString()} items.`}
				</Text>
			) : null}
		</>
	);
}

const MATRIX_SHAPE_KEYS = ['rows', 'field', 'bucket', 'group', 'cell', 'from', 'to'];

/** A report preset as the stored `options.matrix` (the API's spelling). */
function matrixFromReport(report, blank) {
	const matrix = {};
	MATRIX_SHAPE_KEYS.forEach((key) => {
		if (report?.[key] !== undefined && report?.[key] !== null && report?.[key] !== '') matrix[key] = report[key];
	});
	if (blank) matrix.blank = true;
	return matrix;
}

/**
 * The controls' answers as a stored definition's `options`, in the API's
 * spelling (`layout`, `attachment_list`, `attachments`, `matrix`, and Excel's
 * `groups`). It lives
 * beside the controls because the questions and the shape of the answer are
 * one thing.
 *
 * The report grid: a definition whose subject IS the saved filter carrying
 * the preset stores no pivot of its own (`reportFromFilter`) — the run reads
 * the preset live, so editing the filter's report changes the export. The one
 * exception is a blank grid, which has to be stated in `options.matrix`, and
 * the API reads `matrix` whole, so the pivot is copied with it.
 *
 * A definition's OWN stored pivot (an `options.matrix` on a subject with no
 * report — authored through the API) comes back in as `report` with
 * `reportFromFilter: false`, so it is written back whole on every save.
 */
export function buildStoredOptions({
	format,
	contents,
	listLayout = 'table',
	attachmentList,
	embedAttachments,
	blankForm,
	isItemsTarget = false,
	supportsAttachmentOptions = false,
	report = null,
	reportFromFilter = false,
	groupShape = 'item',
}) {
	// Excel's one question: a repeating group one line per entry. The default
	// (one line per item) is the server's and is not written.
	if (format === 'xlsx') return isItemsTarget && groupShape === 'entries' ? { groups: 'entries' } : {};
	if (format !== 'pdf') return {};
	const options = {};
	if (isItemsTarget) {
		options.layout = contents === 'records' || contents === 'matrix' || contents === 'grid' || contents === 'table'
			? contents
			: (listLayout === 'grid' ? 'grid' : 'table');
		if (options.layout === 'matrix' && report && (blankForm || !reportFromFilter)) {
			options.matrix = matrixFromReport(report, blankForm);
		}
	}
	if (supportsAttachmentOptions || (isItemsTarget && contents === 'records')) {
		options.attachment_list = attachmentList !== false;
		options.attachments = embedAttachments === true;
	}
	return options;
}

/** A stored `options` back into the controls' answers (the editor's opening state). */
export function controlsFromStoredOptions(options = {}) {
	const layout = options?.layout;
	return {
		contents: ['records', 'matrix', 'grid'].includes(layout) ? layout : 'table',
		attachmentList: options?.attachment_list !== false,
		embedAttachments: options?.attachments === true,
		blankForm: options?.matrix?.blank === true,
		groupShape: options?.groups === 'entries' ? 'entries' : 'item',
	};
}
